import { useEffect, useMemo, useState } from "react";
import { FORM_DOTS } from "@/lib/preview";
import { ARENA_COLS, ARENA_EXTRA_STATS, groupRows, matchesPosition } from "./arenaData";
import { ArenaControls } from "./ArenaControls";
import { ColumnsMenu } from "./ColumnsMenu";
import { ArenaGroup } from "./ArenaGroup";
import { TeamTotalsCompare } from "./TeamTotals";
import "./LiveRosterArena.css";

const KEY = "arena-hidden-cols-v3";
const LAYOUT_KEY = "arena-layout";
const DEFAULT_HIDDEN = ARENA_EXTRA_STATS;
const readHidden = () => { try { const v = JSON.parse(localStorage.getItem(KEY)); return Array.isArray(v) ? v : DEFAULT_HIDDEN; } catch { return DEFAULT_HIDDEN; } };

export function LiveRosterArena({ rows, embedded }) {
  const [metric, setMetric] = useState("pts");
  const [n, setN] = useState(FORM_DOTS ? 6 : 10);
  const [scope, setScope] = useState("espn");
  const [position, setPosition] = useState("ALL");
  const [layout, setLayoutState] = useState(() => (localStorage.getItem(LAYOUT_KEY) === "team" ? "team" : "role"));
  const setLayout = (l) => { setLayoutState(l); localStorage.setItem(LAYOUT_KEY, l); };
  const [hidden, setHidden] = useState(readHidden);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(hidden)); }, [hidden]);

  // Exactly-6 columns: unchecking hides a stat, checking shows it only while fewer than 6 are visible
  // (so the user swaps one out before adding another). The Form column is permanent and not toggleable.
  const toggle = (k) => setHidden((h) => {
    if (h.includes(k)) {
      const visibleCount = ARENA_COLS.length - h.filter((x) => ARENA_COLS.includes(x)).length;
      return visibleCount >= 6 ? h : h.filter((x) => x !== k);
    }
    return [...h, k];
  });
  const cols = ARENA_COLS.filter((s) => !hidden.includes(s));
  const showForm = true;
  const visible = useMemo(() => rows.filter((r) => matchesPosition(r, position)), [rows, position]);
  const { starters, bench, unverified } = useMemo(() => groupRows(visible), [visible]);
  // Team layout: group players by team, home first then away.
  const teams = useMemo(() => {
    const map = new Map();
    for (const r of visible) {
      if (!map.has(r.team)) map.set(r.team, { code: r.team, logo: r.team_logo, home: r.home, rows: [] });
      map.get(r.team).rows.push(r);
    }
    return [...map.values()].sort((a, b) => (b.home === true) - (a.home === true));
  }, [visible]);
  const shared = { cols, showForm, onHide: toggle, onPickMetric: setMetric, metric, n, scope };

  return (
    <div className={`la-root ${embedded ? "is-embedded" : ""}`} data-testid="live-roster-arena">
      <div className="la-content">
        <ArenaControls position={position} setPosition={setPosition} metric={metric} setMetric={setMetric}
          n={n} setN={setN} scope={scope} setScope={setScope} layout={layout} setLayout={setLayout} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
        {menuOpen && <ColumnsMenu hidden={hidden} toggle={toggle} reset={() => setHidden(DEFAULT_HIDDEN)} />}
        <div className="la-scroll">
          {layout === "team" ? (
            <>
              {teams.map((t) => {
                const g = groupRows(t.rows);
                return (
                  <div className="la-teamblock" key={t.code} data-testid={`arena-teamblock-${t.code}`}>
                    <div className="la-teamhead" data-testid={`arena-teamhead-${t.code}`}>
                      {t.logo && <img src={t.logo} alt="" loading="lazy" />}
                      <b>{t.code}</b><small>{t.home ? "Home" : "Away"} · {t.rows.length}</small>
                    </div>
                    <ArenaGroup kind="starters" title="Starters" rows={g.starters} {...shared} />
                    <ArenaGroup kind="bench" title="Bench" rows={g.bench} {...shared} />
                    {g.unverified.length > 0 && <ArenaGroup kind="unverified" title="Not confirmed" rows={g.unverified} {...shared} />}
                  </div>
                );
              })}
              <TeamTotalsCompare teams={teams} scope={scope} />
            </>
          ) : (
            <>
              <ArenaGroup kind="starters" title="Starters" rows={starters} {...shared} />
              <ArenaGroup kind="bench" title="Bench" rows={bench} {...shared} />
              {unverified.length > 0 && <ArenaGroup kind="unverified" title="Not confirmed" rows={unverified} {...shared} />}
              <TeamTotalsCompare teams={teams} scope={scope} />
            </>
          )}
        </div>
        <p className="la-note">Each cell shows pre-game projection / live — the live value turns green when ahead of pace and red when behind (scaled by minutes played). COLD = clearly behind pace on the selected stat. Live stats from ESPN. Starters and bench: home team first, then away, each by points in this game. Form boxes: green above / red below the player's season average.</p>
      </div>
    </div>
  );
}
